'use client'

import * as React from 'react'
import { ChevronsUpDown } from 'lucide-react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from './dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from './sidebar'

export function WorkspaceSwitcher({
  workspaces,
  getWorkspaceUrl,
}: {
  workspaces: {
    name: string
    logo?: React.ElementType
    desc?: string
  }[]
  getWorkspaceUrl: (name: string) => string
}) {
  const { isMobile } = useSidebar()
  const activeWorkspace = workspaces.find((w) => w.logo) ?? workspaces[0]
  const [isOpen, setIsOpen] = React.useState(false)

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) {
        return
      }

      const keyNumber = parseInt(event.key, 10)
      if (keyNumber >= 1 && keyNumber <= workspaces.length) {
        event.preventDefault()
        const workspaceIndex = keyNumber - 1
        const selectedWorkspace = workspaces[workspaceIndex]
        if (selectedWorkspace) {
          const url = getWorkspaceUrl(selectedWorkspace.name)
          if (url) {
            window.location.href = url
          }
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, workspaces, getWorkspaceUrl])

  if (!activeWorkspace) {
    return null
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              {activeWorkspace.logo && (
                <div className="text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                  <activeWorkspace.logo className="size-4" />
                </div>
              )}
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">
                  {activeWorkspace.name}
                </span>
                {activeWorkspace.desc && (
                  <span className="truncate text-xs">
                    {activeWorkspace.desc}
                  </span>
                )}
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              Switch Workspace
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {workspaces.map((workspace, index) => (
              <DropdownMenuItem
                key={workspace.name}
                onClick={() => {
                  const url = getWorkspaceUrl(workspace.name)
                  if (url) {
                    window.location.href = url
                  }
                }}
                className="gap-2 p-2"
              >
                {workspace.logo && (
                  <workspace.logo className="size-3.5 shrink-0" />
                )}
                {workspace.name}
                <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
